/**
 * clean-beauty-standard.js
 *
 * Sticky scroll-pin technique:
 *   The Shopify <section class="section-clean-beauty"> is a 300vh tall scroll
 *   lane (CSS). The <clean-beauty-section> inside it is position:sticky;top:0;
 *   height:100vh — it stays pinned to the viewport while we scroll through
 *   the 200vh of animation travel.
 *
 * Progress p (0→1) = scrolled past lane top / (lane height − viewport height)
 *
 *   p = 0   lane top hits viewport top  (section just became sticky)
 *   p = 1   lane bottom hits viewport bottom (sticky releases, page continues)
 *
 * Phase 1 — p 0.00→0.65: line2 fades in  (0→1), line1 fades out (1→0)
 * Phase 2 — p 0.65→1.00: line2 fades out (1→0), line1 fades in  (0→1)
 */

class CleanBeautySection extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._rafId = null;
    this._observer = null;
  }

  connectedCallback() {
    this._wrap = this.querySelector('[data-cbs-reveal]');
    this._ingredients = this.querySelector('.c-clean-beauty__ingredients');
    this._items = this.querySelectorAll('.c-clean-beauty__ingredient'); // Targeted items
    this._init();
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.contains(this)) { this._teardown(); this._init(); }
    });
  }

  disconnectedCallback() { this._teardown(); }

  _init() {
    if (!this._wrap) return;
    const lane = this.parentElement || this;
    const line1 = this._wrap.querySelector('.c-clean-beauty__title--line1');
    const line2 = this._wrap.querySelector('.c-clean-beauty__title--line2');

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: 'top 60%', // Inicia cuando el tope de la sección este al 75% del viewport (un cuarto adentro)
        end: 'bottom 70%', // Termina cuando el final este al 25% del tope (casi saliendo)
        scrub: 1,
        invalidateOnRefresh: true
      }
    });

    if (line1) tl.fromTo(line1, { opacity: 0.2 }, { opacity: 1, duration: 0.3, ease: 'none' }, 0);
    if (line2) tl.fromTo(line2, { opacity: 0.2 }, { opacity: 1, duration: 0.3, ease: 'none' }, 0.2);
    
    if (this._items && this._items.length > 0) {
      tl.fromTo(this._items, 
        { opacity: 0, y: 20 }, 
        { 
          opacity: 1, 
          y: 0, 
          duration: 0.4, 
          stagger: 0.1, // Reveal one by one
          ease: 'power1.out' 
        }, 
        0.3
      );
    }

    this._gsapTimeline = tl;
  }

  _teardown() {
    if (this._gsapTimeline) {
      this._gsapTimeline.scrollTrigger?.kill();
      this._gsapTimeline.kill();
      this._gsapTimeline = null;
    }
    window.removeEventListener('scroll', this._onScroll);
    if (this._observer) { this._observer.disconnect(); this._observer = null; }
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }

    // Clear GSAP styles
    const line1 = this._wrap?.querySelector('.c-clean-beauty__title--line1');
    const line2 = this._wrap?.querySelector('.c-clean-beauty__title--line2');
    if (line1) gsap.set(line1, { clearProps: 'all' });
    if (line2) gsap.set(line2, { clearProps: 'all' });
    if (this._ingredients) gsap.set(this._ingredients, { clearProps: 'all' });
    if (this._items) gsap.set(this._items, { clearProps: 'all' });
  }

  _onScroll() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._tick();
    });
  }

  _tick() {
    const lane = this.parentElement || this;
    const rect = lane.getBoundingClientRect();
    const winH = window.innerHeight || document.documentElement.clientHeight;

    // How many px we have scrolled past the top edge of the lane.
    // Negative means the lane hasn't entered the viewport yet.
    const scrolled = -rect.top;

    if (scrolled <= 0) {
      this._setVars(0, 0);
      return;
    }

    const total = rect.height - winH;
    const p = Math.min(1, scrolled / total);

    const l1 = _ramp(p, 0.00, 0.30);
    const l2 = _ramp(p, 0.20, 0.50);

    this._setVars(l1, l2);
  }

  _setVars(l1, l2) {
    const w = this._wrap;
    w.style.setProperty('--line1-opacity', _lerp(0.2, 1, l1).toFixed(4));
    w.style.setProperty('--line2-opacity', _lerp(0.2, 1, l2).toFixed(4));
  }
}

/** Linear ramp: returns 0 before start, 1 after end, linear between. */
function _ramp(p, start, end) {
  if (p <= start) return 0;
  if (p >= end) return 1;
  return (p - start) / (end - start);
}

/** Linear interpolation from a to b by t (0→1). */
function _lerp(a, b, t) {
  return a + (b - a) * t;
}

if (!customElements.get('clean-beauty-section')) {
  customElements.define('clean-beauty-section', CleanBeautySection);
}
